import { FC } from 'react';
import Modal from '@common/modal';
import Icon from '@common/assets';
import {
  MatchHomeHiddenPositionListKeys,
  useHiddenPositions,
  usePositionShow,
  useApplicationPositionList,
  useApplicationStatus,
} from '@repository/match';
import { MatchingHomeHiddenPositionItemVO } from '@domain/vo/match/MatchingHomeHiddenPositionVO';
import { MatchingApplicationStatusType } from '@domain/rs/match/MatchingHomeApplicationPositionRs';
import Colors from '@domain/constant/colors';
import { queryClient } from '@utils/queryClient';

import classnames from 'classnames/bind';
import styles from './hiddenPositionModal.module.scss';
const cx = classnames.bind(styles);

interface HiddenPositionCardProps {
  positionData: MatchingHomeHiddenPositionItemVO;
}

const HiddenPositionCard: FC<HiddenPositionCardProps> = ({ positionData }) => {
  const { mutate: fetchPositionShow } = usePositionShow(async () => {
    await queryClient.refetchQueries(MatchHomeHiddenPositionListKeys, { active: true, exact: true });
  });

  return (
    <div className={cx('cardWrap')} key={positionData.positionSn}>
      <a className={cx('moreBtn')} role="button">
        <span className={cx('hidden')}>hidden resume cancle btn</span>
        <Icon name="moreHorizonLight" width={32} height={32} />
        <div className={cx('btnArea')}>
          <a className={cx('btn')} role="button" onClick={() => fetchPositionShow([positionData.positionSn])}>
            포지션 숨김 해제
          </a>
        </div>
      </a>
      <span className={cx('badge', { jobda: positionData.positionType === 'JOBDA' })}>
        {positionData.positionTypeText}
      </span>
      <strong className={cx('positionName')}>{positionData.positionName}</strong>
      <span className={cx('companyName')}>{positionData.companyName}</span>
    </div>
  );
};

interface HiddenPositionModalProps {
  tabType: MatchingApplicationStatusType;
  visible: boolean;
  onClose: () => void;
}

const HiddenPositionModal: FC<HiddenPositionModalProps> = ({ tabType, visible, onClose }) => {
  //모달이 열릴때 가져오지 않으면, 최신 데이터가 아니므로 enabled옵션 사용
  const { data } = useHiddenPositions(visible);
  const { refetch } = useApplicationPositionList(tabType, { enabled: false });
  const { refetch: refetchApplicationStatus } = useApplicationStatus({ enabled: false });
  const isEmpty = !data?.hiddenPositions.length;

  const getPositionItemEl = () => {
    if (isEmpty)
      return (
        <div className={cx('emptyContent')}>
          <Icon name="joyEmpty" width={96} height={96} />
          <p className={cx('desc')}>숨겨둔 포지션을 찾을 수 없어요.</p>
        </div>
      );

    return data?.hiddenPositions.map((position) => (
      <HiddenPositionCard key={position.positionSn} positionData={position} />
    ));
  };

  const handleClose = () => {
    refetch();
    refetchApplicationStatus();
    onClose();
  };

  return (
    <Modal visible={visible}>
      <div className={cx('layer')}>
        <a className={cx('closeBtn')} role="button" onClick={handleClose}>
          <Icon name="closeLight" width={24} height={24} fill={Colors.C_COOL_GRAY_90} />
        </a>
        <strong className={cx('title')}>숨김 포지션 확인</strong>

        <p className={cx('desc', { hidden: isEmpty })}>
          · 포지션 숨김해제 시, 지원현황에서 다시 해당 지원내역을 확인하실 수 있습니다.
        </p>

        <div className={cx('contentsWrap')}>
          <div className={cx('scrollArea', { isEmpty })}>{getPositionItemEl()}</div>
        </div>

        <a className={cx('confirmBtn')} role="button" onClick={handleClose}>
          확인
        </a>
      </div>
    </Modal>
  );
};

export default HiddenPositionModal;
